import {Inject, Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {UsuarioToken, UsuarioTokenDecoded, UsuarioAutenticado, UsuarioPermissoes} from '../master/models';
import {JwtHelperService} from './jwt-helper-service';
import {LocalStorageUtil} from './util/localstorage-util';

// Parâmetros com que um módulo abre o login da identidade (ver o LoginComponent de cada módulo e o LoginComponentService).
const PARAMETROS_FLUXO_LOGIN = ['redirect_uri', 'aplicacao', 'client_id', 'contaId', 'state'];

const MARGEM_EXPIRACAO_TOKEN_SEGUNDOS = 60;

@Injectable({
    providedIn: 'root',
})
export class LoginService {

    public accessToken: string | null;
    public suporte: boolean = false;
    public token$: Observable<UsuarioToken | null>;
    private tokenBehaviorSubject: BehaviorSubject<UsuarioToken | null>;
    public contaEmUso: any;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private jwtHelper: JwtHelperService,
    ) {
        let accessToken = LocalStorageUtil.obterTokenUsuario();
        let tokenDec: UsuarioToken | null = null;
        try {
            tokenDec = this.decode(accessToken);
        } catch {
            // Token salvo que não pode ser lido (valor corrompido): descartado, como se não houvesse sessão. Sem isso o
            // serviço (injetado em toda a aplicação) não seria criado, ou os guards deixariam passar um token inútil.
            LocalStorageUtil.limparDadosLocaisUsuario();
            accessToken = null;
        }
        this.accessToken = accessToken;

        this.tokenBehaviorSubject = new BehaviorSubject(tokenDec);

        this.token$ = this.tokenBehaviorSubject.asObservable();
    }

    public estaLogado = (): boolean => !!this.accessToken;

    /**
     * Sem token, com o token salvo vencido ou vencendo em menos de 60s (margem para relógio defasado), ou malformado.
     */
    public tokenExpirado = (): boolean => {
        if (!this.accessToken) {
            return true;
        }
        try {
            return this.jwtHelper.isTokenExpired(this.accessToken, MARGEM_EXPIRACAO_TOKEN_SEGUNDOS);
        } catch {
            return true;
        }
    };

    public login = () => this.router.navigateByUrl('login');

    public logout() {
        LocalStorageUtil.limparDadosLocaisUsuario();
        this.tokenBehaviorSubject.next(null);
        this.accessToken = null;
        this.suporte = false;
        this.contaEmUso = null;
        // Mantém só os parâmetros do fluxo de login (no login da identidade aberto por um módulo: redirect_uri, aplicacao,
        // ...) para o usuário voltar ao módulo depois de entrar, mesmo que algo dispare o logout na tela. Lidos do
        // endereço do navegador: a URL do roteador só é atualizada ao fim da primeira navegação.
        const atuais = new URLSearchParams(this.parametrosEnderecoAtual());
        const queryParams: Record<string, string | boolean> = { logout: true };
        // Só no fluxo vindo de um módulo (com redirect_uri): em outras páginas, state/contaId de mesmo nome não são dele.
        if (atuais.get('redirect_uri')) {
            for (const parametro of PARAMETROS_FLUXO_LOGIN) {
                const valor = atuais.get(parametro);
                if (valor) {
                    queryParams[parametro] = valor;
                }
            }
        }
        this.router.navigate(['/login'], { queryParams });
    }

    protected parametrosEnderecoAtual = (): string => window.location.search;

    public salvarToken = (accessToken: string) => {
        let tokenDec: UsuarioToken | null;
        try {
            tokenDec = this.decode(accessToken);
        } catch {
            // Não grava um token que não pode ser lido: a sessão anterior fica intacta e a falha fica visível.
            throw new Error('Token inválido.');
        }
        this.accessToken = accessToken;
        LocalStorageUtil.salvarTokenUsuario(accessToken);
        if (tokenDec) {
            this.tokenBehaviorSubject.next(tokenDec);
        }
    };

    public obterUsuario = (): UsuarioAutenticado =>
        JSON.parse(LocalStorageUtil.obterUsuario()!);

    public salvarUsuario = (usuarioAutenticado: UsuarioAutenticado) =>
        LocalStorageUtil.salvarUsuario(JSON.stringify(usuarioAutenticado));

    public salvarPermissoes = (permissoes: UsuarioPermissoes) =>
        LocalStorageUtil.salvarPermissoes(JSON.stringify(permissoes));

    public obterUsuarioPermissoes = (): UsuarioPermissoes =>
        JSON.parse(LocalStorageUtil.obterPermissoes()!);

    /**
     * Lê o token (null se não houver) e atualiza suporte/contaEmUso. Lança erro se o token ou as claims em JSON
     * (contaEmUso, contratoEmUso) não puderem ser lidos; nesse caso nada é alterado.
     */
    private decode(accessToken: string | null): UsuarioToken | null {
        const tokenDecode = this.jwtHelper.decodeToken<UsuarioTokenDecoded>(accessToken);
        if (!tokenDecode) {
            return null;
        }

        const { sub, usuario, suporte, contaEmUso, contratoEmUso, email } = tokenDecode;
        const conta = contaEmUso && JSON.parse(contaEmUso);
        const contrato = contratoEmUso && JSON.parse(contratoEmUso);

        this.suporte = Boolean(suporte === 'True');
        this.contaEmUso = conta;
        return {
            id: sub, usuario, email,
            suporte: this.suporte,
            contaEmUso: this.contaEmUso,
            contratoEmUso: contrato,
        } as UsuarioToken;
    }
}
